/**
 * Call scripts (ENG-3356) — operator-authored, ordered steps the bot follows
 * on a matching conversation. A script is named by the operator, says when it
 * applies ("use this when…"), and lists steps in the operator's order. Every
 * script in force at a scope is rendered into the system prompt; the model
 * picks the matching one from the descriptions. Scope shape mirrors
 * `RoutingGroupInterface`: a row lives at exactly one of org / group /
 * location, and the nearest scope wins per `slug`.
 */

/** A piece of information the bot must hold before it continues past a step. Platform vocabulary. */
export type CallScriptRequirement =
  | "name"
  | "phone"
  | "email"
  | "location"
  | "unit-size"
  | "move-in-date"
  | "what-storing"
  | "storage-duration"
  | "lead-source"
  | "payment-date";

/**
 * One step of a script (ENG-3402): a reference into the resolved step library by slug, plus the
 * must-haves the operator attached in this script. Custom free-text steps no longer exist — a
 * step's wording lives in the library (platform catalog, overridable per org by Swivl staff).
 */
export interface CallScriptStep {
  /** `CallScriptLibraryStep.slug` — platform, or an org-added step. */
  slug: string;
  requirements: CallScriptRequirement[];
}

/**
 * The shape rows carried before ENG-3402 (`{ kind: "platform", key }` / `{ kind: "custom", text }`).
 * `normalizeCallScriptSteps` (libs/helpers) maps it to `CallScriptStep`; kept only for old
 * version snapshots and the data migration.
 */
export type LegacyCallScriptStep =
  | { kind: "platform"; key: string; requirements?: CallScriptRequirement[] }
  | { kind: "custom"; text: string; requirements?: CallScriptRequirement[] };

/** What a version snapshot captures — everything the prompt is built from. */
export interface CallScriptSnapshot {
  name: string;
  whenToUse: string;
  steps: CallScriptStep[];
  /**
   * "For sequences only" (ENG-3403): the script renders only in a conversation that came from a
   * sequence day naming it (`BotSequenceConfigStep.callScriptSlug`); never in an ordinary
   * conversation. Omitted = false.
   */
  forSequencesOnly?: boolean;
}

export interface CallScriptInterface extends CallScriptSnapshot {
  id: number;
  orgId: number;
  groupId: number | null;
  locationId: number | null;
  /** Stable identity across scopes; derived from the name when the script is first created. */
  slug: string;
  /** Bumped on every save; optimistic lock and the version-history key. */
  revision: number;
  updatedByMemberId: number | null;
  createdAt?: Date | string;
  updatedAt?: Date | string;
}

export interface CallScriptVersionInterface {
  id: number;
  orgId: number;
  callScriptId: number;
  revision: number;
  snapshot: CallScriptSnapshot;
  memberId: number | null;
  /** "saved" | "restored from revision N" | "removed here" | "reverted to inherited" */
  note: string;
  createdAt?: Date | string;
}

/**
 * One entry of the org-wide call-script history (Chuck, 2026-10-01): a script or step version row
 * with its subject attached, so changes and deletions stay visible after the script or step is gone.
 */
export interface CallScriptHistoryEntryBase {
  /** The version row's id — unique within its `kind`. */
  id: number;
  /** When the change was recorded (ISO). */
  at: string;
  memberId: number | null;
  /** The version row's note: "saved", "restored from revision N", "removed here", "reverted to inherited", "reverted to default", "deleted". */
  note: string;
  revision: number;
  /** The script or step row no longer exists (reverted to inherited / deleted). */
  subjectDeleted: boolean;
  slug: string;
}

export interface CallScriptHistoryScriptEntry extends CallScriptHistoryEntryBase {
  kind: "script";
  callScriptId: number;
  groupId: number | null;
  locationId: number | null;
  snapshot: CallScriptSnapshot;
}

export interface CallScriptHistoryStepEntry extends CallScriptHistoryEntryBase {
  kind: "step";
  callScriptStepId: number;
  snapshot: CallScriptStepSnapshot;
}

export type CallScriptHistoryEntry = CallScriptHistoryScriptEntry | CallScriptHistoryStepEntry;

/** One page of history, newest first. `nextBefore` is the cursor for the next page; null when this is the last. */
export interface CallScriptHistoryPage {
  entries: CallScriptHistoryEntry[];
  nextBefore: string | null;
}

export const CALL_SCRIPT_HISTORY_PAGE_SIZE = 100;

export type CallScriptSource = "platform" | "org" | "group" | "location";

/** A script in force at a scope after inheritance is applied. */
export interface ResolvedCallScript extends CallScriptSnapshot {
  slug: string;
  source: CallScriptSource;
  /** The row that won, null for the platform default. */
  rowId: number | null;
  /** Set when the conversation came from a sequence day bound to this script — rendered first (ENG-3403). */
  startingScript?: boolean;
}

/** Where a script is bound as a sequence day's starting script — read-only "Used by" on the script (ENG-3403). */
export interface CallScriptSequenceUse {
  sequenceType: string;
  /** The ladder step's stable key. */
  stepKey: string;
  offsetDays: number;
  /** Which scope's ladder carries the binding. */
  scope: { groupId: number | null; locationId: number | null };
}

/** One rendered script for the bot's prompt section. */
export interface CallScriptPromptEntry {
  slug: string;
  name: string;
  text: string;
}

export interface CallScriptUpsertBody extends CallScriptSnapshot {
  /** Omitted on first create — derived from `name`. Required to override an inherited script. */
  slug?: string;
  groupId?: number | null;
  locationId?: number | null;
  /** The revision the editor loaded; a mismatch means someone else saved first. */
  expectedRevision?: number;
}

export const CALL_SCRIPT_MAX_PER_SCOPE = 8;
export const CALL_SCRIPT_NAME_MAX_CHARS = 60;
export const CALL_SCRIPT_WHEN_TO_USE_MAX_CHARS = 240;
/** A step's bot sentence (was the custom-step limit). */
export const CALL_SCRIPT_STEP_SENTENCE_MAX_CHARS = 240;
export const CALL_SCRIPT_STEP_LABEL_MAX_CHARS = 60;
export const CALL_SCRIPT_STEP_AGENT_COPY_MAX_CHARS = 200;
export const CALL_SCRIPT_STEP_CRITERIA_MAX_CHARS = 240;

// ─── Step library rows (ENG-3402) ───────────────────────────────────────────

/** What a step row (and its version snapshots) carries — every overridable field of a library step. */
export interface CallScriptStepSnapshot {
  group: string;
  label: string;
  sentence: string;
  liveAgentCopy: string;
  coachingCompletionCriteria: string;
  showToAgents: boolean;
  requirementsAllowed: boolean;
  defaultRequirements: CallScriptRequirement[];
}

/**
 * An org's step row (`call_script_step`, org scope only, Swivl staff): a platform step's slug
 * overrides that step for the org; any other slug is an org-added step.
 */
export interface CallScriptStepInterface extends CallScriptStepSnapshot {
  id: number;
  orgId: number;
  slug: string;
  revision: number;
  updatedByMemberId: number | null;
  createdAt?: Date | string;
  updatedAt?: Date | string;
}

export interface CallScriptStepVersionInterface {
  id: number;
  orgId: number;
  callScriptStepId: number;
  revision: number;
  snapshot: CallScriptStepSnapshot;
  memberId: number | null;
  /** "saved" | "restored from revision N" | "reverted to default" | "deleted" */
  note: string;
  createdAt?: Date | string;
}

export interface CallScriptStepUpsertBody extends CallScriptStepSnapshot {
  /** Omitted on first create of an org step — derived from `label`. Required to override a platform step. */
  slug?: string;
  expectedRevision?: number;
}

/** A library step as resolved for an org: the platform entry, an org override of it, or an org-added step. */
export type CallScriptStepSource = "platform" | "org";
/** Hard budget for the whole call-scripts prompt section (~1,900 tokens). A ceiling, not a target — most orgs use a fraction (Chuck, 2026-09-24). */
export const CALL_SCRIPTS_PROMPT_MAX_CHARS = 8000;

// ─── Agents' Script section (ENG-3402) ──────────────────────────────────────

/** One step as the Contact Center's Script section shows it. */
export interface CallScriptAgentStep {
  slug: string;
  label: string;
  liveAgentCopy: string;
  done: boolean;
  /** Message index at which the coaching engine saw it completed. */
  doneAtMessageIndex?: number;
}

/** The script in play for a conversation, as agents see it: only steps with `showToAgents`, in script order. */
export interface CallScriptAgentView {
  scriptSlug: string;
  name: string;
  steps: CallScriptAgentStep[];
  /** Steps the engine has judged done, including ones hidden from agents. */
  completedCount: number;
  totalCount: number;
}
